import React, { useEffect } from 'react';
import { Tour, Typography } from 'antd';
import type { TourProps } from 'antd';

const { Paragraph, Text } = Typography;

// First-run guided tour for the Triage page. Anchors to [data-tour] markers
// on the three panes, the likely-cause callout, and the action bar so the
// steps stay attached even as those components are refactored.
//
// Targets are resolved lazily (() => element) because the panes mount after
// a location/issue is selected; a missing target makes Tour center that step
// instead of throwing, so the tour still completes on an empty queue.
const target = (selector: string) => () =>
  document.querySelector(selector) as HTMLElement | null;

export const TRIAGE_TOUR_VERSION = 'v1';
const TOUR_KEY = 'triageTourSeen';

export function hasSeenTour(): boolean {
  try {
    return localStorage.getItem(TOUR_KEY) === TRIAGE_TOUR_VERSION;
  } catch {
    return true; // storage blocked → don't nag
  }
}

export function markTourSeen(): void {
  try {
    localStorage.setItem(TOUR_KEY, TRIAGE_TOUR_VERSION);
  } catch {
    /* ignore */
  }
}

interface TriageTourProps {
  open: boolean;
  onClose: () => void;
}

const TriageTour: React.FC<TriageTourProps> = ({ open, onClose }) => {
  // Escape hatch. antd's Tour renders a full-viewport mask but gives the user
  // no keyboard/mask way out — the only exits (✕ / Next / Finish) live inside
  // the popover. If that popover fails to paint on some client (seen in the
  // field: grey screen, no popup, ESC did nothing), the mask traps the user
  // entirely. Wire ESC and a mask click to onClose so the mask can never be a
  // dead-end regardless of whether the popover rendered.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onPointerDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.classList?.contains('ant-tour-mask')) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, onClose]);

  const steps: TourProps['steps'] = [
    {
      title: 'Welcome to Fleet Triage',
      description: (
        <Paragraph style={{ marginBottom: 0 }}>
          This tool turns the firehose of raw alerts into{' '}
          <Text strong>one row per real problem</Text> and suggests a likely
          cause. Your job: confirm or correct it. ~5 steps, 30 seconds.
        </Paragraph>
      ),
      target: null, // centered intro
    },
    {
      title: '1 · Locations',
      description:
        'Every location with at least one open suggestion. Pick one to load its queue. The number is how many issues are waiting there.',
      target: target('[data-tour="locations"]'),
    },
    {
      title: '2 · Issue list',
      description:
        'One row per real problem (collapsed from all its raw alerts), with battery/signal sparklines. Use ↑ / ↓ to move between them.',
      target: target('[data-tour="issues"]'),
    },
    {
      title: '3 · Likely cause + evidence',
      description: (
        <Paragraph style={{ marginBottom: 0 }}>
          The headline tells you the rule&rsquo;s best guess and why. Below it: the
          battery <Text strong>at the moment it went offline</Text> (not just
          "now"), the pre-offline signal trend (degrading vs sudden cliff), and
          whether nearby nodes failed too.
        </Paragraph>
      ),
      target: target('[data-tour="likely-cause"]'),
    },
    {
      title: '4 · Classify it',
      description: (
        <Paragraph style={{ marginBottom: 0 }}>
          Pick a class with the buttons or press{' '}
          <Text keyboard>1</Text>–<Text keyboard>8</Text>. The ★ button is the
          classifier&rsquo;s suggestion. <Text strong>Every choice you make trains
          the agent</Text> — until operators label issues, it runs blind.
        </Paragraph>
      ),
      target: target('[data-tour="action-bar"]'),
    },
    {
      title: 'That\'s it',
      description: (
        <Paragraph style={{ marginBottom: 0 }}>
          Confirm accepts the suggestion as-is; Reclassify re-runs the rule on
          fresh signals. Open <Text strong>Help</Text> any time for the full
          9-class glossary and keyboard shortcuts. Happy triaging.
        </Paragraph>
      ),
      target: null,
    },
  ];

  return (
    <Tour
      open={open}
      onClose={onClose}
      onFinish={onClose}
      steps={steps}
      // Don't let a half-loaded pane (missing target) wedge the tour.
      gap={{ offset: 6 }}
    />
  );
};

export default TriageTour;
